<template>
  <div class="flex flex-col w-full h-full p-3">
    <div class="bg-gradient-to-r from-blue-50 to-transparent dark:from-gray-800 dark:to-transparent rounded-xl p-6 mb-6">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {{ $t("Select Objects to Map") }}
      </h2>
      <div class="space-y-2 text-gray-600 dark:text-gray-400">
        <p class="flex items-start">
          <Info class="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
          <span>
            {{ $t("Here you will find all the standard and custom objects that you have available in your") }}
            <span class="font-medium text-gray-900 dark:text-white mx-1">{{ integration.title }}</span>
          </span>
        </p>
        <p class="flex items-start">
          <FileCheck class="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
          <span>{{ $t("On this step you will select which of these objects you want to map into RevSetter") }}</span>
        </p>
        <p class="flex items-start">
          <Clock class="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
          <span>{{
            $t("You will select and finish the flow for one object at a time, but you can always map more objects before finishing.")
          }}</span>
        </p>
      </div>
    </div>

    <div class="flex items-start justify-between mt-6 mb-2">
      <div class="relative w-full max-w-sm">
        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <Search class="w-5 h-5 text-gray-400" aria-hidden="true" />
        </div>
        <input
          v-model="filter"
          autocomplete="off"
          type="text"
          class="block w-full pl-10 pr-3 py-2 text-sm border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          :placeholder="$t('Search by object name')">
      </div>
    </div>

    <transition-group name="fade" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-4 overflow-y-auto" tag="div">
      <div
        v-for="(obj, index) in filtered_loaded_objects"
        :key="obj.name"
        class="group relative flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-6 dark:bg-dark-box-background dark:border-gray-700 animate__animated animate__fadeInUp min-h-[280px]"
        :style="{ animationDelay: Math.min(index * 0.002, 1) + 's' }">
        <!-- Mapped Status Badge -->
        <div
          v-if="mapped_objects.find((name) => name == obj.name)"
          class="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1 bg-green-50 rounded-full">
          <CircleCheck class="w-4 h-4 text-green-500" aria-hidden="true" />
          <span class="text-xs font-medium text-green-700">{{ $t("Mapped") }}</span>
        </div>

        <!-- Sync Status Badge -->
        <div
          v-if="mapped_objects.find((name) => name == obj.name) && integrations_sync_stats?.[integration_name]?.[obj.name]"
          class="absolute top-4 left-4 flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 rounded-full">
          <span class="flex h-2 w-2 relative">
            <span class="bg-blue-500 animate-ping opacity-80 absolute inline-flex w-full h-full rounded-full" />
            <span class="bg-blue-500 relative inline-flex rounded-full h-2 w-2" />
          </span>
          <span class="text-xs font-medium text-blue-700">
            {{ integrations_sync_stats?.[integration_name]?.[obj.name]?.synced }}/{{
              integrations_sync_stats?.[integration_name]?.[obj.name]?.total
            }}
          </span>
        </div>

        <!-- Card Content -->
        <div class="flex flex-col flex-1">
          <!-- Icon and Title -->
          <div class="flex flex-col items-center mb-4">
            <div class="flex justify-center mb-3 text-gray-400">
              <i :class="defaultIconCache[obj.name]" class="text-2xl" aria-hidden="true" />
            </div>
            <h3
              class="text-lg font-semibold text-center text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors duration-200">
              {{ obj.label }}
              <span class="block text-sm font-normal text-gray-500 mt-1">({{ obj.name }})</span>
            </h3>
          </div>

          <!-- Record Count -->
          <div v-if="obj?.meta?.row_count" class="text-center text-sm text-gray-600 mb-4">
            {{ $t("Records") }}: <span class="font-medium">{{ $utils.humanizeNumber(obj.meta?.row_count) || "" }}</span>
          </div>

          <!-- Mapping Info -->
          <div v-if="mapped_objects.find((name) => name == obj.name)" class="flex-1">
            <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {{ $t("Mapped to") }}:
            </h4>
            <ul class="space-y-1.5">
              <li
                v-for="int_obj in currentMapping.direct_objects
                  .concat(currentMapping.cross_objects)
                  .filter((o) => o.object_name == obj.name)"
                :key="int_obj.id"
                class="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <span class="truncate">
                  {{ $store.state.object_types.find((o) => o.type == int_obj.object_type)?.label }}
                </span>
                <span
                  :class="[
                    'ml-1.5 px-1.5 py-0.5 rounded text-xs font-medium',
                    int_obj.is_direct ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-600'
                  ]">
                  {{ int_obj.is_direct ? $t("Primary") : $t("Secondary") }}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex gap-2">
          <button
            type="button"
            class="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors duration-200"
            @click="mapObject(obj)">
            {{ mapped_objects.find((name) => name == obj.name) ? $t("Adjust Mapping") : $t("Map") }}
          </button>
          <button
            v-if="isObjectMapped(obj.name)"
            class="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors duration-200"
            @click="reSyncServiceObject(obj)">
            {{ $t("ReSync") }}
          </button>
        </div>
      </div>
    </transition-group>

    <!-- Load More Button -->
    <div v-if="loaded_objects.length < objects.length && !filter" class="flex justify-center py-6">
      <button
        type="button"
        :style="{ animationDelay: Math.min(loaded_objects.length * 0.02, 2) + 's' }"
        class="group relative flex items-center justify-center px-6 py-3 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 dark:bg-dark-box-background dark:border-gray-700 animate__animated animate__fadeInUp"
        @click="loadMore">
        <div
          class="absolute left-0 top-0 bottom-0 bg-gradient-to-r from-blue-50 to-transparent w-24 rounded-l-xl transition-opacity duration-200 opacity-0 group-hover:opacity-100" />
        <CirclePlus class="w-5 h-5 mr-3 text-blue-600" aria-hidden="true" />
        <span class="text-sm font-medium text-gray-900 dark:text-white">
          {{ $t("Load More Objects") }}
          <span class="text-sm font-normal text-gray-500 ml-1.5"> ({{ loaded_objects.length }}/{{ objects.length }}) </span>
        </span>
        <div
          class="absolute right-0 top-0 bottom-0 bg-gradient-to-l from-blue-50 to-transparent w-24 rounded-r-xl transition-opacity duration-200 opacity-0 group-hover:opacity-100" />
      </button>
    </div>
  </div>
</template>

<script>
import { Search, CircleCheck, Info, CirclePlus, FileCheck, Clock } from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import integrationService from "@/services/integration.service"

export default {
  name: "DataObjectSelection",
  components: {
    CircleCheck,
    CirclePlus,
    Search,
    Info,
    FileCheck,
    Clock
  },
  props: {
    objects: {
      type: Array,
      default: () => [],
      required: true
    },
    integration: {
      type: Object,
      required: true
    },
    currentMapping: {
      type: Object,
      default: () => ({}),
      required: false
    }
  },
  emits: ["objectSelected", "editMapping"],
  data() {
    return {
      integration_name: "",
      filter: "",
      defaultIconCache: {},
      recently_accessed_objects: [],
      mapped_objects: [],
      salesforce_standard_objects: [
        "Account",
        "Contact",
        "Lead",
        "Opportunity",
        "Task",
        "Event",
        "Case",
        "Campaign",
        "User",
        "Asset",
        "Contract",
        "Order",
        "OrderItem",
        "Quote"
      ],
      hubspot_standard_objects: [
        "Contact",
        "Company",
        "Deal",
        "Ticket",
        "Engagement",
        "Product",
        "Line Item",
        "Quote",
        "Activity",
        "Task",
        "Note",
        "Email",
        "Call",
        "Meeting"
      ],
      default_objects: [],
      loaded_objects: []
    }
  },
  computed: {
    integrations_sync_stats() {
      return this.$store.state.integration.integrations_sync_stats
    },
    filtered_loaded_objects() {
      if (this.filter) {
        return this.objects.filter((f) => f.label.toLowerCase().includes(this.filter.toLowerCase()))
      } else {
        return this.loaded_objects
      }
    }
  },
  async created() {
    this.integration_name = this.$route.params.integration_name

    this.register_listeners()
    this.default_objects = this.integration_name === "salesforce" ? this.salesforce_standard_objects : this.hubspot_standard_objects

    this.objects.forEach((object) => {
      if (this.isObjectMapped(object.name)) {
        if (!this.mapped_objects.includes(object.name)) {
          this.mapped_objects.push(object.name)
        }
      }
    })
    this.recently_accessed_objects =
      this.$store.state.ui_options.integrations_recently_accessed_objects?.[this.$route.params.integration_reference] ??
      this.objects.map((o) => o.name).slice(0, 10)

    this.loaded_objects = this.objects
      .filter(
        (o) =>
          o?.meta?.row_count > 1 ||
          this.mapped_objects.includes(o.name) ||
          this.recently_accessed_objects.includes(o.name) ||
          this.default_objects.includes(o.name)
      )
      .sort(
        (a, b) =>
          this.mapped_objects.includes(b.name) - this.mapped_objects.includes(a.name) ||
          (this.recently_accessed_objects.indexOf(a.name) >= 0 ? this.recently_accessed_objects.indexOf(a.name) : Infinity) -
            (this.recently_accessed_objects.indexOf(b.name) >= 0 ? this.recently_accessed_objects.indexOf(b.name) : Infinity)
      )
      .slice(0, 10)
  },
  unmounted() {
    this.unregister_listeners()
  },
  methods: {
    reSyncServiceObject(obj) {
      integrationService
        .reSyncServiceObjects(this.$route.params.integration_reference, {
          target_objects_names: [obj.name]
        })
        .then(() => {
          this.showToast(this.$t("ReSync of") + ` ${obj.label} ` + this.$t("has been started"), "success")
        })
    },
    showToast(message, type) {
      this.$toast.open({
        message: message,
        position: "top",
        type: type,
        duration: 3000
      })
    },
    mapObject(object) {
      this.addToRecentlyAccessed(object)
      let integration_path = `/admin/integrations/${this.$route.params.integration_name}/${this.$route.params.integration_reference}`
      this.$store.commit("ui/setCurrentAdminIntegrationsBreadcrumb", [
        {
          label: this.$route.params.integration_name.capitalize(),
          link: integration_path
        },
        {
          label: this.$t("Objects"),
          link: `${integration_path}/object-selection`
        },
        {
          label: object.label,
          link: `${integration_path}/mapping?obj=${object.name}`
        }
      ])
      // append to the router obj=object.name without triggering a page reload so we use history.pushState and the url to mapping
      let url = new URL(window.location.href.replace("/object-selection", "/mapping-type"))
      url.searchParams.set("obj", object.name)
      window.history.pushState({}, "", url)

      this.$emit("objectSelected", object)
    },
    addToRecentlyAccessed(object) {
      let t_index = this.recently_accessed_objects.findIndex((t) => t == object.name)
      if (t_index != -1) {
        this.recently_accessed_objects.splice(t_index, 1)
      }
      if (t_index != -1) {
        this.recently_accessed_objects.splice(t_index, 1)
      }
      this.recently_accessed_objects.unshift(object.name)
      let all_recently_accessed = this.$store.state.ui_options.integrations_recently_accessed_objects || {}
      all_recently_accessed[this.$route.params.integration_reference + "." + this.$route.params.instance_number] =
        this.recently_accessed_objects
      this.$store.dispatch("ui/setUIValue", {
        integrations_recently_accessed_objects: all_recently_accessed
      })
    },
    isObjectMapped(object_name) {
      return (
        this.currentMapping &&
        (this.currentMapping?.direct_objects?.map((o) => o.object_name).includes(object_name) ||
          this.currentMapping?.cross_objects?.map((o) => o.object_name).includes(object_name))
      )
    },
    register_listeners() {
      this.$emitter.on(`${ALL_EMITTERS.OBJECT_CHECKED}${this.integration_name.capitalize()}`, (object_name) => {
        this.mapped_objects.push(object_name)
      })
    },
    unregister_listeners() {
      this.$emitter.off(`${ALL_EMITTERS.OBJECT_CHECKED}${this.integration_name.capitalize()}`)
    },
    loadMore() {
      // Load by 5
      const additional_objects = this.objects
        .filter((o) => !this.loaded_objects.includes(o) && !this.mapped_objects.includes(o.name))
        .slice(0, 5)
      this.loaded_objects = this.loaded_objects.concat(additional_objects)
    }
  }
}
</script>

<style>
.list-move, /* apply transition to moving elements */
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.list-leave-active {
  position: absolute;
}
</style>
